import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, MapPin, Clock, Bike, Car, Truck, Zap, Sparkles, Leaf } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/24376862/pexels-photo-24376862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PARTNER_IMG = "https://images.pexels.com/photos/34337558/pexels-photo-34337558.jpeg";

const vehicles = [
  { icon: Bike, label: "Two-Wheeler", desc: "Bikes, scooters, mopeds" },
  { icon: Car, label: "Four-Wheeler", desc: "Cars, SUVs, taxis" },
  { icon: Truck, label: "Commercial", desc: "Trucks, buses, tempos" },
  { icon: Zap, label: "Three-Wheeler", desc: "Autos, e-rickshaws" },
];

const stats = [
  { k: "12K+", v: "PUC issued" },
  { k: "480+", v: "Partner centers" },
  { k: "26", v: "States covered" },
  { k: "< 15 min", v: "Avg wait time" },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-mesh">
        <div className="absolute top-32 -left-16 w-72 h-72 rounded-full bg-primary/20 blur-3xl animate-drift" aria-hidden />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-emerald-200/40 blur-3xl animate-drift" aria-hidden />

        <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <motion.div variants={container} initial="hidden" animate="show" className="md:col-span-7">
            <motion.div variants={item} className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1.5 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">India&apos;s PUC aggregator</span>
            </motion.div>
            <motion.h1 variants={item} className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[1] text-foreground">
              Clean air.
              <br />
              <span className="bg-gradient-to-r from-primary via-cyan-500 to-emerald-500 bg-clip-text text-transparent gradient-animated">Clear certificate.</span>
              <br />
              <span className="text-muted-foreground">Zero queues.</span>
            </motion.h1>
            <motion.p variants={item} className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Book a Pollution Under Control test at a certified centre near you in under a minute. For every vehicle — two-wheelers to heavy commercial fleets.
            </motion.p>
            <motion.div variants={item} className="mt-10 flex flex-wrap gap-3">
              <Link to="/book" data-testid="hero-book-btn">
                <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-14 px-8 text-base shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow duration-300">
                  Book PUC Test <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-partner-btn">
                <Button size="lg" variant="outline" className="rounded-full border-border text-foreground hover:bg-secondary h-14 px-8 text-base">
                  Partner With Us
                </Button>
              </Link>
            </motion.div>

            <motion.div variants={item} className="mt-14 flex items-center gap-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> RTO Certified</div>
              <div className="flex items-center gap-2"><Leaf className="w-4 h-4 text-emerald-500" /> Instant certificate</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> 60-second booking</div>
            </motion.div>
          </motion.div>

          {/* Animated hero visual */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="md:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-primary/10">
              <img src={HERO_IMG} alt="Clean mobility" className="absolute inset-0 w-full h-full object-cover animate-float-slow" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
            </div>
            {/* Floating cards */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }} className="absolute -left-4 top-10 bg-white rounded-2xl shadow-xl border border-border p-4 w-56 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center pulse-ring"><ShieldCheck className="w-5 h-5 text-primary" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Certificate</div>
                  <div className="text-sm font-semibold">Issued in 15 min</div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }} className="absolute -right-4 bottom-16 bg-white rounded-2xl shadow-xl border border-border p-4 w-56 animate-float-slow">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Next available slot</div>
              <div className="text-sm font-semibold">Today · 10:30 AM</div>
              <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full w-2/3 bg-gradient-to-r from-primary to-emerald-400" /></div>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-border bg-white/70 backdrop-blur">
          <div className="mx-auto max-w-7xl px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
            {stats.map((s) => (
              <div key={s.v} className="py-6 px-2 text-center">
                <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground">{s.k}</div>
                <div className="text-xs uppercase tracking-[0.15em] mt-1 text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle types */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">01 — Coverage</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Every vehicle on Indian roads.</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-base text-muted-foreground leading-relaxed">
                From your first scooter to a 40-ton hauler, VayuCheck partners are certified for every RTO category — petrol, diesel, CNG, LPG, hybrid, and EV compliance checks.
              </p>
            </div>
          </div>

          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="vehicle-grid">
            {vehicles.map((v) => (
              <motion.div
                key={v.label}
                variants={item}
                whileHover={{ y: -6 }}
                className="relative bg-white border border-border rounded-2xl p-8 hover:border-primary hover:shadow-xl hover:shadow-primary/10 transition-shadow duration-300 group cursor-default"
                data-testid={`vehicle-card-${v.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                  <v.icon className="w-7 h-7 text-primary" />
                </div>
                <div className="font-display text-xl font-bold tracking-tight">{v.label}</div>
                <div className="text-sm text-muted-foreground mt-2">{v.desc}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Cities strip */}
      <section className="border-b border-border bg-mesh-soft">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">02 — Cities</div>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">PUC testing, city by city.</h2>
            <p className="text-sm text-muted-foreground max-w-md">Dedicated pages for every metro. Book, filter, and browse local RTO-certified centres.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cities-strip">
            {[
              { slug: "bengaluru", name: "Bengaluru" },
              { slug: "mumbai", name: "Mumbai" },
              { slug: "delhi", name: "Delhi NCR" },
              { slug: "pune", name: "Pune" },
              { slug: "hyderabad", name: "Hyderabad" },
              { slug: "chennai", name: "Chennai" },
            ].map((c) => (
              <Link key={c.slug} to={`/puc/${c.slug}`} data-testid={`city-link-${c.slug}`} className="group">
                <div className="bg-white border border-border rounded-xl p-5 hover:border-primary hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">PUC in</div>
                  <div className="font-display text-xl font-bold tracking-tight group-hover:text-primary transition-colors duration-200">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">Browse centres <ArrowRight className="w-3 h-3" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">03 — Process</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-16 max-w-2xl">Three steps, no paperwork, one certificate.</h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", icon: MapPin, title: "Find a centre", body: "Filter by city or pincode. See real availability at every partner near you." },
              { n: "02", icon: Clock, title: "Book a slot", body: "Pick a time. Enter your vehicle details. Skip the walk-in queue entirely." },
              { n: "03", icon: ShieldCheck, title: "Get certified", body: "Drive in, get tested, drive out with a valid PUC certificate on the spot." },
            ].map((step) => (
              <motion.div key={step.n} whileHover={{ y: -4 }} className="bg-white border border-border rounded-2xl p-10 hover:shadow-xl hover:shadow-primary/5 transition-shadow duration-300">
                <div className="flex items-center justify-between mb-8">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{step.n}</div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><step.icon className="w-6 h-6 text-primary" /></div>
                </div>
                <div className="font-display text-2xl font-bold tracking-tight mb-3">{step.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner + Fleet CTAs */}
      <section className="border-b border-border bg-mesh-soft">
        <div className="mx-auto max-w-7xl px-6 py-24 grid gap-6">
          <div className="grid md:grid-cols-2 rounded-3xl overflow-hidden bg-white border border-border shadow-lg">
            <div className="relative min-h-[320px] hidden md:block">
              <img src={PARTNER_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/60" />
            </div>
            <div className="p-10 md:p-14">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">Own a PUC centre?</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">List your workshop. Get bookings on autopilot.</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8">Zero setup fees. Verified customers. Real-time slot management. Join hundreds of RTO-approved centres already growing with VayuCheck.</p>
              <Link to="/register?role=partner" data-testid="cta-partner-register">
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-6">Register your centre <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/5 via-white to-emerald-50 p-10 md:p-14">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-primary/15 blur-3xl animate-drift" aria-hidden />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">Fleet operators & transporters</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4 max-w-3xl">Never track a truck&apos;s PUC in a spreadsheet again.</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8 max-w-2xl">Bulk-register your fleet, get automated 3-day expiry reminders across every vehicle, and book renewals at any partner centre in one click. Priority slots for verified commercial accounts.</p>
              <Link to="/register/commercial" data-testid="cta-commercial-register">
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-6 shadow-lg shadow-primary/25">Register your fleet <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} VayuCheck — India&apos;s PUC aggregator</div>
        <div className="flex gap-6 flex-wrap">
          <Link to="/about" data-testid="footer-about" className="hover:text-foreground">About</Link>
          <Link to="/faqs" data-testid="footer-faqs" className="hover:text-foreground">FAQs</Link>
          <Link to="/contact" data-testid="footer-contact" className="hover:text-foreground">Contact</Link>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-foreground">Privacy</Link>
          <span>Made in Bharat</span>
        </div>
      </footer>
    </div>
  );
}
